import { z } from "zod";
import { UNIT_SYMBOLS } from "./units";

/* ── 공통 열거형 ─────────────────────────────────────────────── */

/** 설비 분류. 과업지시서: 생산,저장,이송 (+ 보조설비). 일반 LCA 에서는 분류와 무관하게 한 시스템으로 구성한다 */
export const STAGES = ["production", "storage", "transport", "auxiliary"] as const;
export const StageSchema = z.enum(STAGES);
export type Stage = z.infer<typeof StageSchema>;
export const STAGE_LABEL: Record<Stage, string> = {
  production: "생산",
  storage: "저장",
  transport: "이송",
  auxiliary: "보조",
};

/** 산정 방식. general = 일반 LCA 기반, cert = 국내 청정수소 인증제 모의산정 */
export const METHODS = ["general", "cert"] as const;
export const MethodSchema = z.enum(METHODS);
export type MethodId = z.infer<typeof MethodSchema>;
export const METHOD_LABEL: Record<MethodId, string> = {
  general: "일반 LCA 기반 산정",
  cert: "청정수소 인증제 모의산정",
};

/** 설비가 서는 자리 , 인증제 모의산정의 범위(원료 조달 ~ 생산시설 출하) 판정에 쓴다 */
export const LOCATIONS = ["upstream", "onsite", "downstream"] as const;
export const LocationSchema = z.enum(LOCATIONS);
export type Location = z.infer<typeof LocationSchema>;
export const LOCATION_LABEL: Record<Location, string> = {
  upstream: "원료 조달(상류)",
  onsite: "생산시설 내부",
  downstream: "출하 이후(하류)",
};

/** 입력값의 출처 유형. 과업지시서: 기본값,사용자 입력값,추정값,자료 미입력을 구분해 표시 */
export const VALUE_TYPES = ["default", "user", "estimated", "missing"] as const;
export const ValueTypeSchema = z.enum(VALUE_TYPES);
export type ValueType = z.infer<typeof ValueTypeSchema>;
export const VALUE_TYPE_LABEL: Record<ValueType, string> = {
  default: "기본값",
  user: "사용자 입력",
  estimated: "추정값",
  missing: "자료 미입력",
};

/** 시스템 경계 제외 사유 */
export const EXCLUSION_REASONS = ["outside_boundary", "negligible", "no_data", "cert_scope", "other"] as const;
export const ExclusionReasonSchema = z.enum(EXCLUSION_REASONS);
export type ExclusionReason = z.infer<typeof ExclusionReasonSchema>;
export const EXCLUSION_LABEL: Record<ExclusionReason, string> = {
  outside_boundary: "시스템 경계 밖",
  negligible: "기여도 미미",
  no_data: "자료 미입력",
  cert_scope: "인증제 산정범위 밖",
  other: "기타",
};

/**
 * 활동자료의 종류 , 어떤 배출계수를 붙일지 정한다.
 * electricity 는 배출계수를 직접 갖지 않고 시나리오의 전력 믹스에서 계수를 받는다.
 * parameter 는 계산에 쓰지 않는 참고값(운송거리,적재량 등)이다 , LCI 에 기록만 한다.
 */
export const ACTIVITY_KINDS = [
  "electricity",
  "natural_gas",
  "natural_gas_upstream",
  "diesel",
  "steam",
  "water",
  "chemical",
  "hydrogen_vent",
  "parameter",
  "other",
] as const;
export const ActivityKindSchema = z.enum(ACTIVITY_KINDS);
export type ActivityKind = z.infer<typeof ActivityKindSchema>;
export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  electricity: "전력",
  natural_gas: "천연가스(연소,원료)",
  natural_gas_upstream: "천연가스 공급망(상류)",
  diesel: "경유",
  steam: "공정열,스팀",
  water: "용수",
  chemical: "약품,소모품",
  hydrogen_vent: "수소 손실(배출)",
  parameter: "참고값(계산 제외)",
  other: "기타",
};

export const H2_STATES = ["gas", "liquid"] as const;
export const H2StateSchema = z.enum(H2_STATES);
export type H2State = z.infer<typeof H2StateSchema>;

export const ALLOCATION_POLICIES = ["none", "mass", "energy"] as const;
export const AllocationPolicySchema = z.enum(ALLOCATION_POLICIES);
export type AllocationPolicy = z.infer<typeof AllocationPolicySchema>;
export const ALLOCATION_LABEL: Record<AllocationPolicy, string> = {
  none: "배분 없음(전량 수소 귀속)",
  mass: "질량 기준 배분",
  energy: "에너지(발열량) 기준 배분",
};

/* ── 설비와 활동자료 ─────────────────────────────────────────── */

export const ActivityInputSchema = z.object({
  id: z.string().min(1),
  /** 템플릿의 입력 키(사용자 정의는 자유) , 예시값 비교,도움말 연결에 쓴다 */
  key: z.string().min(1),
  label: z.string().min(1),
  kind: ActivityKindSchema,
  amount: z.number().nullable(),
  unit: z.enum(UNIT_SYMBOLS),
  valueType: ValueTypeSchema,
  required: z.boolean().default(true),
  /** 값의 출처(기본값이면 자료 출처, 사용자값이면 실측 근거) */
  source: z.string().optional(),
  /** 특정 배출계수를 강제할 때(사용자 정의 설비). 없으면 kind 로 기본 계수를 찾는다 */
  efId: z.string().optional(),
  /** 사용자 정의 배출계수(값,단위,출처) , efId 보다 우선 */
  customEf: z
    .object({ value: z.number().nonnegative(), unit: z.string().min(1), source: z.string().min(1), baseYear: z.number().int().optional() })
    .optional(),
  note: z.string().optional(),
});
export type ActivityInput = z.infer<typeof ActivityInputSchema>;

/** 설비를 지나는 수소 흐름 */
export const H2FlowSchema = z.object({
  /** producer: 수소를 만든다(전해조,개질기), passthrough: 받아서 넘긴다(압축,저장,이송), none: 수소가 지나지 않는다(순수제조,보조) */
  role: z.enum(["producer", "passthrough", "none"]),
  inKg: z.number().nonnegative().nullable().default(null),
  outKg: z.number().nonnegative().nullable().default(null),
  lossKg: z.number().nonnegative().nullable().default(null),
});
export type H2Flow = z.infer<typeof H2FlowSchema>;

export const EquipmentSchema = z.object({
  id: z.string().min(1),
  /** 기본 설비 템플릿 id. 사용자 정의는 "custom" */
  templateId: z.string().min(1),
  name: z.string().min(1),
  stage: StageSchema,
  /** 생산 방식(대상기술) , 설비 팔레트에서 어떤 기술의 설비였는지 */
  techId: z.string().optional(),
  location: LocationSchema,
  /** 공정 순서(0부터). 수소 흐름은 이 순서를 따른다 */
  order: z.number().int().nonnegative(),
  inBoundary: z.boolean(),
  exclusionReason: ExclusionReasonSchema.optional(),
  exclusionNote: z.string().optional(),
  inputs: z.array(ActivityInputSchema),
  h2: H2FlowSchema,
  note: z.string().optional(),
});
export type Equipment = z.infer<typeof EquipmentSchema>;

/* ── 시나리오 ────────────────────────────────────────────────── */

export const ElectricitySourceShareSchema = z.object({
  sourceId: z.string().min(1),
  /** 0~1. mode=share 일 때 쓴다 */
  share: z.number().min(0).max(1).default(0),
  /** mode=kWh 일 때 , 여기서 비율을 계산한다 */
  kWh: z.number().nonnegative().optional(),
});

export const ElectricityMixSchema = z.object({
  mode: z.enum(["share", "kWh"]).default("share"),
  sources: z.array(ElectricitySourceShareSchema).min(1),
  /** 전력원 입력량의 측정기간·경계와 설비 합계 차이의 근거 */
  note: z.string().optional(),
});
export type ElectricityMix = z.infer<typeof ElectricityMixSchema>;

export const FunctionalUnitSchema = z.object({
  /** 기능단위 수소량. 기본 1 kg */
  h2Kg: z.number().positive().default(1),
  purityPct: z.number().min(0).max(100),
  pressureBar: z.number().nonnegative(),
  state: H2StateSchema,
  deliveryPoint: z.string().min(1),
});
export type FunctionalUnit = z.infer<typeof FunctionalUnitSchema>;

export const ByproductSchema = z.object({
  name: z.string().min(1),
  amountKg: z.number().nonnegative(),
  /** 에너지 배분에 쓰는 저위발열량 MJ/kg. 산소는 0 */
  lhvMJperKg: z.number().nonnegative().default(0),
});

export const AllocationSchema = z.object({
  policy: AllocationPolicySchema.default("none"),
  byproducts: z.array(ByproductSchema).default([]),
  note: z.string().optional(),
});
export type Allocation = z.infer<typeof AllocationSchema>;

export const ScenarioSchema = z.object({
  schemaVersion: z.literal(1).default(1),
  id: z.string().optional(),
  name: z.string().min(1),
  /** 교육용 예시에서 출발했다면 그 id , 기준결과값 비교에 쓴다 */
  exampleId: z.string().optional(),
  meta: z.object({
    purpose: z.string().default(""),
    baseYear: z.number().int().min(2000).max(2100),
    author: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  }),
  method: MethodSchema,
  basis: z.object({
    /** 대표 생산방식(대상기술) */
    productionTechId: z.string().min(1),
    /** 다른 생산기술 설비를 유지할 때의 명시적 검토 기록. 구성 변경 시 재검토한다. */
    technologyReview: z.object({ productionTechId: z.string(), equipmentIds: z.array(z.string()), note: z.string().min(1) }).optional(),
    /** 산정 기준 수소 생산량(kg). 모든 설비 입력값은 이 양을 만드는 동안의 사용량이다 */
    batchH2Kg: z.number().positive(),
    /** 참고 정보 , 연간 생산량,운전시간 */
    annualOutputKg: z.number().nonnegative().optional(),
    operatingHoursPerYear: z.number().nonnegative().optional(),
  }),
  functionalUnit: FunctionalUnitSchema,
  electricity: ElectricityMixSchema,
  allocation: AllocationSchema,
  equipment: z.array(EquipmentSchema),
  notes: z.string().optional(),
});
export type Scenario = z.infer<typeof ScenarioSchema>;

/** 저장,불러오기 파일 봉투 , 파일 형식 판별과 버전 관리 */
export const ScenarioFileSchema = z.object({
  app: z.literal("h2lca-edu"),
  fileVersion: z.literal(1),
  exportedAt: z.string(),
  scenario: ScenarioSchema,
});
export type ScenarioFile = z.infer<typeof ScenarioFileSchema>;
